/**
 * HTTP layer for community chat hooks.
 *
 * Centralizes endpoint paths so a route change touches one file and so
 * hooks can be tested by mocking this module rather than the global
 * `apiClient` (which is shared with the rest of the app).
 */
import { apiClient } from "@shared/lib/api/client";
import type {
  ChatAttachment,
  ChatMemberSummary,
  ChatMessage,
  ConversationDetail,
  SendMessagePayload,
} from "../types/chat";

export interface PaginatedResponse<T> {
  results: T[];
  nextCursor?: string | null;
  next?: string | null;
}

export interface AttachmentRecord extends ChatAttachment {
  conversationId: string;
}

export interface UnreadSummary {
  totalUnread: number;
  perConversation: Record<string, number>;
}

export interface PresenceRecord {
  userId: string;
  status: "online" | "offline" | "unknown";
  lastSeenAt: string | null;
}

interface SidecarConversation {
  id: string;
  type: "dm" | "group";
  title: string | null;
  memberUserIds?: string[];
  metadata?: Record<string, unknown>;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  updatedAt: string;
  memberCount?: number;
}

export interface SidecarMessage {
  id: string;
  clientId?: string | null;
  conversationId: string;
  senderUserId: string;
  status?: string;
  deliveryStatus?: ChatMessage["status"];
  body: string | null;
  attachments?: ChatAttachment[];
  reactions?: Array<{ emoji: string; userId: string; createdAt: string }>;
  replyTo?: {
    messageId: string;
    senderUserId: string;
    bodyPreview: string | null;
  } | null;
  editedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

function memberProfilesFromMetadata(metadata: Record<string, unknown> | undefined) {
  const raw = metadata?.memberProfiles;
  if (!Array.isArray(raw)) return new Map<string, ChatMemberSummary>();
  const entries = raw
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const row = item as Record<string, unknown>;
      if (typeof row.userId !== "string") return null;
      return [
        row.userId,
        {
          userId: row.userId,
          displayName:
            typeof row.displayName === "string" && row.displayName
              ? row.displayName
              : row.userId,
          avatarUrl: typeof row.avatarUrl === "string" ? row.avatarUrl : null,
        } satisfies ChatMemberSummary,
      ] as const;
    })
    .filter((entry): entry is readonly [string, ChatMemberSummary] => Boolean(entry));
  return new Map(entries);
}

function fallbackMember(userId: string): ChatMemberSummary {
  return {
    userId,
    displayName: userId.includes("@") ? userId : `User ${userId.slice(0, 8)}`,
    avatarUrl: null,
  };
}

function mapConversation(raw: SidecarConversation): ConversationDetail {
  const profiles = memberProfilesFromMetadata(raw.metadata);
  const members = (raw.memberUserIds ?? []).map(
    (userId) => profiles.get(userId) ?? fallbackMember(userId),
  );
  return {
    id: raw.id,
    type: raw.type,
    title: raw.title,
    members,
    memberCount: raw.memberCount,
    lastMessage: raw.lastMessageAt
      ? {
          id: raw.id,
          senderUserId: "",
          preview: raw.lastMessagePreview ?? "",
          sentAt: raw.lastMessageAt,
        }
      : null,
    updatedAt: raw.updatedAt,
  };
}

function groupReactions(
  reactions: SidecarMessage["reactions"] = [],
): ChatMessage["reactions"] {
  const grouped = new Map<string, Set<string>>();
  for (const reaction of reactions) {
    const set = grouped.get(reaction.emoji) ?? new Set<string>();
    set.add(reaction.userId);
    grouped.set(reaction.emoji, set);
  }
  return Array.from(grouped.entries()).map(([emoji, userIds]) => ({
    emoji,
    count: userIds.size,
    reactorUserIds: Array.from(userIds),
    selfReacted: false,
  }));
}

export function mapSidecarMessage(raw: SidecarMessage): ChatMessage {
  return {
    id: raw.id,
    clientId: raw.clientId ?? null,
    conversationId: raw.conversationId,
    senderUserId: raw.senderUserId,
    senderDisplayName: fallbackMember(raw.senderUserId).displayName,
    senderAvatarUrl: null,
    body: raw.body ?? "",
    attachments: raw.attachments ?? [],
    sentAt: raw.createdAt,
    editedAt: raw.editedAt,
    deletedAt: raw.deletedAt,
    status: normalizeDeliveryStatus(raw.deliveryStatus),
    replyTo: raw.replyTo
      ? {
          id: raw.replyTo.messageId,
          senderUserId: raw.replyTo.senderUserId,
          senderDisplayName: fallbackMember(raw.replyTo.senderUserId).displayName,
          preview: raw.replyTo.bodyPreview ?? "",
        }
      : null,
    reactions: groupReactions(raw.reactions),
  };
}

function normalizeDeliveryStatus(
  status: SidecarMessage["deliveryStatus"] | undefined,
): ChatMessage["status"] {
  if (
    status === "sending" ||
    status === "sent" ||
    status === "delivered" ||
    status === "read" ||
    status === "failed"
  ) {
    return status;
  }
  return "sent";
}

function mapList<TIn, TOut>(
  payload: {
    results?: TIn[];
    items?: TIn[];
    nextCursor?: string | null;
    next?: string | null;
  },
  mapper: (item: TIn) => TOut,
): PaginatedResponse<TOut> {
  return {
    results: (payload.results ?? payload.items ?? []).map(mapper),
    nextCursor: payload.nextCursor ?? payload.next ?? null,
    next: payload.next ?? payload.nextCursor ?? null,
  };
}

function normalizeUsers(payload: unknown, selfUserId?: string): ChatMemberSummary[] {
  const container =
    payload && typeof payload === "object" && "results" in payload
      ? (payload as { results?: unknown[] }).results
      : payload && typeof payload === "object" && "data" in payload
        ? (payload as { data?: unknown[] }).data
        : Array.isArray(payload)
          ? payload
          : [];

  return (container ?? [])
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const row = item as Record<string, unknown>;
      const userId = String(row.id ?? row.user_id ?? "");
      if (!userId || userId === selfUserId) return null;
      const displayName = String(
        row.fullname ??
          row.full_name ??
          row.username ??
          row.email ??
          `User ${userId.slice(0, 8)}`,
      );
      return {
        userId,
        displayName,
        avatarUrl: typeof row.avatar_url === "string" ? row.avatar_url : null,
      } satisfies ChatMemberSummary;
    })
    .filter((item): item is ChatMemberSummary => Boolean(item));
}

function toSidecarMessage(payload: SendMessagePayload) {
  const attachments = payload.attachments ?? [];
  const first = attachments[0];
  const type = attachments.length
    ? first?.mimeType.startsWith("image/")
      ? "image"
      : "file"
    : "text";
  return {
    type,
    body: payload.body || undefined,
    attachments: attachments.length ? attachments : undefined,
    clientId: payload.clientId,
    replyToMessageId: payload.replyToId,
  };
}

function mapReportReason(reason: string): string {
  if (reason === "hate" || reason === "sexual" || reason === "violence") {
    return "unsafe_content";
  }
  return reason;
}

export const chatApi = {
  listConversations(cursor: string | null) {
    const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
    return apiClient
      .get<{
        items?: SidecarConversation[];
        results?: SidecarConversation[];
        nextCursor?: string | null;
        next?: string | null;
      }>(`/api/chat/conversations${qs}`)
      .then((payload) => mapList(payload, mapConversation));
  },

  createConversation(payload: {
    type: "dm" | "group";
    memberUserIds: string[];
    title?: string;
    memberProfiles?: ChatMemberSummary[];
  }) {
    return apiClient
      .post<SidecarConversation>("/api/chat/conversations", {
        type: payload.type,
        memberUserIds: payload.memberUserIds,
        title: payload.title,
        metadata: payload.memberProfiles
          ? { memberProfiles: payload.memberProfiles }
          : undefined,
      })
      .then(mapConversation);
  },

  getConversation(id: string) {
    return apiClient.get<SidecarConversation>(`/api/chat/conversations/${id}`).then(mapConversation);
  },

  patchConversation(id: string, body: { title?: string }) {
    return apiClient
      .patch<SidecarConversation>(`/api/chat/conversations/${id}`, body)
      .then(mapConversation);
  },

  leaveConversation(id: string) {
    return apiClient.post<void>(`/api/chat/conversations/${id}/leave`);
  },

  listMessages(conversationId: string, cursor: string | null, limit = 50) {
    const params = new URLSearchParams();
    if (cursor) params.set("before", cursor);
    params.set("limit", String(limit));
    return apiClient
      .get<{
        items?: SidecarMessage[];
        results?: SidecarMessage[];
        nextCursor?: string | null;
        next?: string | null;
      }>(`/api/chat/conversations/${conversationId}/messages?${params.toString()}`)
      .then((payload) => {
        const page = mapList(payload, mapSidecarMessage);
        return { ...page, results: [...page.results].reverse() };
      });
  },

  sendMessage(conversationId: string, payload: SendMessagePayload) {
    return apiClient
      .post<SidecarMessage>(
        `/api/chat/conversations/${conversationId}/messages`,
        toSidecarMessage(payload),
      )
      .then(mapSidecarMessage);
  },

  editMessage(messageId: string, body: string) {
    return apiClient
      .patch<SidecarMessage>(`/api/chat/messages/${messageId}`, { body })
      .then(mapSidecarMessage);
  },

  deleteMessage(messageId: string) {
    return apiClient
      .post<SidecarMessage>(`/api/chat/messages/${messageId}/delete`)
      .then(mapSidecarMessage);
  },

  toggleReaction(messageId: string, emoji: string) {
    return apiClient
      .post<SidecarMessage>(`/api/chat/messages/${messageId}/reactions`, { emoji })
      .then(mapSidecarMessage);
  },

  reportMessage(
    messageId: string,
    payload: { reason: string; details?: string },
  ) {
    return apiClient.post<void>(`/api/chat/messages/${messageId}/report`, {
      ...payload,
      reason: mapReportReason(payload.reason),
    });
  },

  blockUser(userId: string) {
    return apiClient.post<void>("/api/chat/blocks", { blockedUserId: userId });
  },

  unblockUser(userId: string) {
    return apiClient.delete<void>(`/api/chat/blocks/${userId}`);
  },

  muteConversation(conversationId: string, durationSeconds: number | null) {
    return apiClient.post<void>(`/api/chat/conversations/${conversationId}/mute`, {
      durationSeconds,
    });
  },

  unmuteConversation(conversationId: string) {
    return apiClient.delete<void>(`/api/chat/conversations/${conversationId}/mute`);
  },

  markRead(conversationId: string, lastReadMessageId: string) {
    return apiClient.post<void>(`/api/chat/conversations/${conversationId}/read`, {
      lastMessageId: lastReadMessageId,
    });
  },

  unreadSummary() {
    return apiClient
      .get<{
        total?: number;
        conversations?: Array<{ conversationId: string; unread: number }>;
      }>("/api/chat/unread-summary")
      .then((payload) => ({
        totalUnread: payload.total ?? 0,
        perConversation: Object.fromEntries(
          (payload.conversations ?? []).map((row) => [
            row.conversationId,
            row.unread,
          ]),
        ),
      }));
  },

  listAttachments(_conversationId: string) {
    return Promise.resolve({ results: [], nextCursor: null });
  },

  getPresence(userId: string) {
    return Promise.resolve({
      userId,
      status: "unknown" as const,
      lastSeenAt: null,
    });
  },

  searchMembers(query: string, selfUserId?: string) {
    const qs = new URLSearchParams({
      search: query,
      is_active: "true",
    });
    return apiClient
      .get<unknown>(`/api/v1/users/?${qs.toString()}`)
      .then((payload) => normalizeUsers(payload, selfUserId));
  },
};
